import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/hooks/useAuth";
import { PageContainer } from "@/components/layout/PageContainer";

type Row = {
  id: string; user_id: string; full_name: string; issuing_region: string; license_number: string | null;
  credential_type: string; credential_description: string | null; file_path: string; status: string;
  reviewer_notes: string | null; lookup_result: string | null; created_at: string;
  license_expires_on?: string | null; attested_at?: string | null;
};

// Official license lookups the reviewer can use. Add more states as they're confirmed.
const OFFICIAL_LOOKUPS: Record<string, string> = {
  Tennessee: "https://verify.tn.gov/",
};

export default function AdminHPEVerificationsPage() {
  const { isAdmin, loading } = useAdmin();
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState("pending");
  const [images, setImages] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [lookup, setLookup] = useState<Record<string, string>>({});

  const load = async () => {
    let q = supabase.from("hpe_license_verifications" as any).select("*").order("created_at", { ascending: false }).limit(200);
    if (filter !== "all") q = q.eq("status", filter);
    const { data } = await q;
    setRows((data ?? []) as unknown as Row[]);
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin, filter]);

  const view = async (r: Row) => {
    const { data } = await supabase.storage.from("hpe-verification-docs").createSignedUrl(r.file_path, 300);
    if (data?.signedUrl) setImages((m) => ({ ...m, [r.id]: data.signedUrl }));
  };

  const decide = async (r: Row, status: "approved" | "rejected" | "needs_info") => {
    if (status === "approved" && r.credential_type === "license" && !(lookup[r.id] ?? "").trim()) {
      toast.error("Record what the official lookup showed before approving.");
      return;
    }
    const { error } = await supabase.from("hpe_license_verifications" as any).update({
      status, reviewer_notes: notes[r.id] || null, lookup_result: lookup[r.id] || null,
      reviewed_by: user?.id, reviewed_at: new Date().toISOString(),
    }).eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success(`Marked ${status.replace("_", " ")}`); load(); }
  };

  if (loading) return null;
  if (!isAdmin) return <PageContainer className="p-8 text-foreground">Reviewers only.</PageContainer>;

  return (
    <PageContainer className="bg-background px-5 pt-8 pb-24">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <h1 className="font-display text-2xl text-foreground">HPE Professional Verifications</h1>
        <div className="flex gap-2">
          {["pending", "needs_info", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`rounded-full border px-3 py-1 font-body text-xs ${filter === f ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
        {rows.length === 0 && <p className="font-body text-sm text-muted-foreground">Nothing here.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-display text-lg text-foreground">{r.full_name}</p>
              <span className="font-body text-xs uppercase text-muted-foreground">{r.status} · {new Date(r.created_at).toLocaleString()}</span>
            </div>
            <p className="font-body text-sm text-foreground">
              {r.credential_type === "license" ? "License" : "Other credential"} · {r.issuing_region}
              {r.license_number ? ` · #${r.license_number}` : ""}
              {r.license_expires_on ? ` · expires ${r.license_expires_on}` : ""}
              {r.attested_at ? " · self-attested (not independently verified)" : ""}
            </p>
            {r.credential_description && <p className="font-body text-sm text-muted-foreground">{r.credential_description}</p>}
            {OFFICIAL_LOOKUPS[r.issuing_region] ? (
              <a href={OFFICIAL_LOOKUPS[r.issuing_region]} target="_blank" rel="noreferrer" className="font-body text-xs text-primary underline">
                Open official {r.issuing_region} license lookup
              </a>
            ) : (
              <p className="font-body text-xs text-muted-foreground">No official lookup linked for this state/country — verify manually with the issuing board.</p>
            )}
            {!r.file_path ? null : images[r.id] ? (
              <img src={images[r.id]} alt="Submitted credential" className="max-h-96 w-full rounded-xl border border-border object-contain" />
            ) : (
              <button onClick={() => view(r)} className="self-start rounded-lg border border-border px-3 py-1.5 font-body text-xs text-foreground">View image</button>
            )}
            {r.lookup_result && <p className="font-body text-xs text-muted-foreground">Lookup: {r.lookup_result}</p>}
            {r.reviewer_notes && <p className="font-body text-xs text-muted-foreground">Notes: {r.reviewer_notes}</p>}
            <input className="rounded-lg border border-border bg-background px-3 py-2 font-body text-sm text-foreground"
              placeholder="Official lookup result (e.g. Active, Cosmetologist, expires 2027-04)"
              value={lookup[r.id] ?? ""} onChange={(e) => setLookup((m) => ({ ...m, [r.id]: e.target.value }))} />
            <input className="rounded-lg border border-border bg-background px-3 py-2 font-body text-sm text-foreground"
              placeholder="Note to the stylist (shown if more info is needed or rejected)"
              value={notes[r.id] ?? ""} onChange={(e) => setNotes((m) => ({ ...m, [r.id]: e.target.value }))} />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => decide(r, "approved")} className="rounded-lg bg-primary px-4 py-2 font-body text-xs font-semibold text-primary-foreground">Approve</button>
              <button onClick={() => decide(r, "needs_info")} className="rounded-lg border border-border px-4 py-2 font-body text-xs text-foreground">Needs more info</button>
              <button onClick={() => decide(r, "rejected")} className="rounded-lg border border-destructive px-4 py-2 font-body text-xs text-destructive">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </PageContainer>
  );
}
